import { useCallback, useEffect } from "react";
import { getCenterFromElement } from "../utils/getCenterFromElement";

/**
 * Hook para manejar el diseño y tamaño del grid
 */
export const useGridLayout = ({ gridRef, tileRefs, setUserAnswers }) => {

  const updateGridSize = useCallback(() => {
    const gridNode = gridRef.current;
    if (!gridNode) return;

    const computedStyle = window.getComputedStyle(gridNode);
    const width = parseFloat(computedStyle.getPropertyValue("width"));
    const gridSize = parseFloat(computedStyle.getPropertyValue("--gridSize"));
    
    if (!isFinite(width) || !isFinite(gridSize) || gridSize <= 0) return;
    
    const diameter = (width / gridSize) * 0.5;
    gridNode.style.setProperty("font-size", `${diameter}px`);
  }, [gridRef]);

  const updateSelectionsPosition = useCallback(() => {
    setUserAnswers((old) => {
      return old.map((selection) => {
        const originTile = tileRefs.current[selection.origin.id];
        const destinationTile = tileRefs.current[selection.destination.id];
        
        if (!originTile || !destinationTile) {
          return selection;
        }
        
        return {
          ...selection,
          center: getCenterFromElement(originTile),
          target: getCenterFromElement(destinationTile),
        };
      });
    });
  }, [setUserAnswers, tileRefs]);

  useEffect(() => {
    let resizeTimeout;
    let fullscreenTimeout;

    const updateAll = () => {
      updateSelectionsPosition();
      updateGridSize();
    };

    const resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateAll, 50);
    };

    const fullscreenHandler = () => {
      clearTimeout(fullscreenTimeout);
      fullscreenTimeout = setTimeout(updateAll, 100);
    };

    const fullscreenEvents = [
      'fullscreenchange',
      'webkitfullscreenchange', 
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];

    updateGridSize();
    
    window.addEventListener("resize", resizeHandler);
    fullscreenEvents.forEach(event => {
      document.addEventListener(event, fullscreenHandler);
    });

    return () => {
      clearTimeout(resizeTimeout);
      clearTimeout(fullscreenTimeout);
      
      window.removeEventListener("resize", resizeHandler);
      fullscreenEvents.forEach(event => {
        document.removeEventListener(event, fullscreenHandler);
      });
    };
  }, [updateGridSize, updateSelectionsPosition]);

  return {
    updateGridSize,
    updateSelectionsPosition
  };
};
